<template>
  <dashboard-layout title="Client Logs">
    <template #actions>
      <search-box v-model="searchQuery" placeholder="Search errors…" />
      <search-box v-model="excludeQuery" placeholder="Exclude text…" />
      <search-box v-model="buildNumberFilter" placeholder="Build number…" />
    </template>

    <stat-grid>
      <stat-card label="Total logs" :value="totalLogs.toLocaleString('en-US')" icon="terminal" accent />
      <stat-card label="Failed" :value="failedCount.toLocaleString('en-US')" icon="zap" />
      <stat-card label="Success" :value="successCount.toLocaleString('en-US')" icon="activity" />
      <stat-card label="Distinct users" :value="distinctUsers.toLocaleString('en-US')" icon="users" />
    </stat-grid>

    <data-panel flush>
      <template #title>Logs <span class="muted">({{ filteredLogs.length.toLocaleString('en-US') }})</span></template>
      <template #actions>
        <filter-chips v-model="errorFilter" :options="errorFilterOptions" />
      </template>

      <loading-state v-if="loading && logs.length === 0" label="Loading errors…" />
      <div v-else class="logs-scroll" ref="tableContainer" @scroll="handleScroll">
        <data-table
          :columns="columns"
          :rows="filteredLogs"
          row-key="id"
          clickable
          @row-click="goToDetail"
        >
          <template #cell-service="{ row }">
            <span class="service">{{ row.service || '—' }}</span>
          </template>
          <template #cell-status="{ row }">
            <status-badge :variant="statusVariant(row.status)" :label="row.status || '—'" />
          </template>
          <template #cell-error="{ row }">
            <pre v-if="parseErrorDescription(row.error_message) !== '-'" class="metadata-pre">{{ parseErrorDescription(row.error_message) }}</pre>
            <span v-else>—</span>
          </template>
          <template #cell-additional="{ row }">
            <span class="additional-info">{{ formatAdditionalInfo(row.error_message) }}</span>
          </template>
          <template #cell-appVersion="{ row }">
            <span class="app-version">{{ parseAppVersion(row.error_message) }}</span>
          </template>
          <template #cell-build="{ row }">
            <span class="build-number">{{ parseBuildNumber(row.error_message) }}</span>
          </template>
          <template #cell-device="{ row }">
            <span class="device">{{ parseDevice(row.error_message) }}</span>
          </template>
          <template #cell-userId="{ row }">
            <router-link :to="`/user/${row.user_id}`" class="user-id-link" @click.stop>
              {{ row.user_id || '—' }}
            </router-link>
          </template>
          <template #cell-date="{ row }">
            <span class="nowrap">{{ formatTimestamp(row.created_at) }}</span>
          </template>
          <template #empty>No errors found</template>
        </data-table>

        <div v-if="loadingMore" class="loading-more">
          <div class="spinner"></div>
          <span>Loading more logs...</span>
        </div>
        <div v-if="!hasMore && filteredLogs.length > 0" class="end-message">
          No more logs to load
        </div>
      </div>
    </data-panel>
  </dashboard-layout>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { FirebaseLogService } from '../services/firebaseLogService'
import DashboardLayout from './ui/DashboardLayout.vue'
import StatGrid from './ui/StatGrid.vue'
import StatCard from './ui/StatCard.vue'
import DataPanel from './ui/DataPanel.vue'
import DataTable from './ui/DataTable.vue'
import SearchBox from './ui/SearchBox.vue'
import FilterChips from './ui/FilterChips.vue'
import LoadingState from './ui/LoadingState.vue'
import StatusBadge from './ui/StatusBadge.vue'

const columns = [
  { key: 'service', label: 'Service', width: '120px' },
  { key: 'status', label: 'Status', width: '90px' },
  { key: 'error', label: 'Error' },
  { key: 'additional', label: 'Additional Info', width: '200px' },
  { key: 'appVersion', label: 'App Version', width: '110px' },
  { key: 'build', label: 'Build', width: '90px' },
  { key: 'device', label: 'Device', width: '150px' },
  { key: 'userId', label: 'User', width: '140px' },
  { key: 'date', label: 'Date', width: '150px' }
]

export default {
  name: 'ClientLogsDashboard',
  components: {
    DashboardLayout, StatGrid, StatCard, DataPanel, DataTable, SearchBox, FilterChips, LoadingState, StatusBadge
  },
  setup() {
    const router = useRouter()
    const service = new FirebaseLogService()
    const logs = ref([])
    const searchQuery = ref('')
    const excludeQuery = ref('')
    const buildNumberFilter = ref('')
    const loading = ref(true)
    const loadingMore = ref(false)
    const hasMore = ref(true)
    const lastDoc = ref(null)
    const tableContainer = ref(null)
    const showEmptyErrors = ref(true)
    const isBuildFiltering = ref(false)

    const parseJSON = (str) => {
      try {
        return JSON.parse(str)
      } catch {
        return null
      }
    }

    const parseErrorDescription = (errorMessage) => {
      if (!errorMessage) return '-'
      const parsed = parseJSON(errorMessage)
      if (parsed && parsed.error) {
        return JSON.stringify(parsed.error, null, 2)
      }
      return errorMessage
    }

    const formatAdditionalInfo = (errorMessage) => {
      if (!errorMessage) return '-'
      const parsed = parseJSON(errorMessage)
      if (parsed && parsed.additionalInfo) {
        const info = Object.entries(parsed.additionalInfo)
          .map(([key, value]) => `${key}: ${value}`)
          .join(', ')
        return info || '-'
      }
      return '-'
    }

    const parseAppVersion = (errorMessage) => {
      if (!errorMessage) return '-'
      const parsed = parseJSON(errorMessage)
      if (parsed && parsed.app && parsed.app.version) {
        return parsed.app.version
      }
      return '-'
    }

    const parseBuildNumber = (errorMessage) => {
      if (!errorMessage) return '-'
      const parsed = parseJSON(errorMessage)
      if (parsed && parsed.app && parsed.app.buildNumber) {
        return parsed.app.buildNumber
      }
      return '-'
    }

    const parseDevice = (errorMessage) => {
      if (!errorMessage) return '-'
      const parsed = parseJSON(errorMessage)
      if (parsed && parsed.device) {
        const model = parsed.device.model || ''
        const system = parsed.device.systemName || ''
        const version = parsed.device.systemVersion || ''
        return `${model} ${system} ${version}`.trim() || '-'
      }
      return '-'
    }

    const filteredLogs = computed(() => {
      const q = searchQuery.value.toLowerCase()
      const excludeQ = excludeQuery.value.toLowerCase()
      const buildNum = buildNumberFilter.value.trim()

      return logs.value.filter(l => {
        const searchMatch = !q ||
          (l.service || '').toLowerCase().includes(q) ||
          (l.status || '').toLowerCase().includes(q) ||
          (l.error_message || '').toLowerCase().includes(q) ||
          (l.user_id || '').toLowerCase().includes(q)

        const errorFilterMatch = showEmptyErrors.value || (l.error_message && l.error_message.trim() !== '')

        const excludeMatch = !excludeQ || !(l.error_message || '').toLowerCase().includes(excludeQ)

        const buildMatch = !buildNum || parseBuildNumber(l.error_message) === buildNum

        return searchMatch && errorFilterMatch && excludeMatch && buildMatch
      })
    })

    const totalLogs = computed(() => logs.value.length)
    const failedCount = computed(() => logs.value.filter(l => l.status === 'failed').length)
    const successCount = computed(() => logs.value.filter(l => l.status === 'success').length)
    const distinctUsers = computed(() => new Set(logs.value.map(l => l.user_id).filter(Boolean)).size)

    const errorFilterOptions = computed(() => {
      const withError = logs.value.filter(l => l.error_message && l.error_message.trim() !== '').length
      return [
        { value: 'all', label: 'All', count: logs.value.length },
        { value: 'nonempty', label: 'With error', count: withError }
      ]
    })

    const errorFilter = computed({
      get: () => (showEmptyErrors.value ? 'all' : 'nonempty'),
      set: (v) => { showEmptyErrors.value = v === 'all' }
    })

    const statusVariant = (status) => {
      if (status === 'failed') return 'danger'
      if (status === 'success') return 'success'
      return 'neutral'
    }

    const loadLogs = async () => {
      loading.value = true
      try {
        const result = await service.getLogsPaginated(40)
        logs.value = result.logs
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
      } catch (e) {
        console.error('Error loading logs:', e)
      } finally {
        loading.value = false
      }
    }

    const loadMore = async () => {
      if (loadingMore.value || !hasMore.value) return

      loadingMore.value = true
      try {
        const result = await service.getLogsPaginated(40, lastDoc.value)
        logs.value = [...logs.value, ...result.logs]
        lastDoc.value = result.lastVisible
        hasMore.value = result.hasMore
      } finally {
        loadingMore.value = false
      }
    }

    const handleScroll = (e) => {
      if (isBuildFiltering.value) return

      const element = e.target
      const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight

      if (scrollPercentage > 0.8 && !loadingMore.value && hasMore.value) {
        loadMore()
      }
    }

    const handleBuildFilterChange = async () => {
      const buildNum = buildNumberFilter.value.trim()

      if (buildNum) {
        isBuildFiltering.value = true
        loading.value = true
        try {
          const allLogs = await service.getAllLogs()
          logs.value = allLogs
          hasMore.value = false
        } catch (e) {
          console.error('Error loading all logs:', e)
        } finally {
          loading.value = false
        }
      } else {
        isBuildFiltering.value = false
        await loadLogs()
      }
    }

    watch(buildNumberFilter, handleBuildFilterChange)

    const goToDetail = (log, event) => {
      if (event.metaKey || event.ctrlKey) {
        window.open(`/client-log/${log.id}`, '_blank')
      } else {
        router.push(`/client-log/${log.id}`)
      }
    }

    const truncateText = (text, max) => {
      if (!text) return '-'
      return text.length > max ? text.substring(0, max) + '...' : text
    }

    const formatTimestamp = ts => {
      if (!ts) return '-'
      if (ts.seconds) {
        const d = new Date(ts.seconds * 1000)
        return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      }
      const d = new Date(ts)
      return d.toLocaleString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    }

    onMounted(loadLogs)

    return {
      logs,
      filteredLogs,
      searchQuery,
      excludeQuery,
      buildNumberFilter,
      errorFilter,
      errorFilterOptions,
      loading,
      loadingMore,
      hasMore,
      tableContainer,
      columns,
      totalLogs,
      failedCount,
      successCount,
      distinctUsers,
      statusVariant,
      handleScroll,
      goToDetail,
      truncateText,
      formatTimestamp,
      parseErrorDescription,
      formatAdditionalInfo,
      parseAppVersion,
      parseBuildNumber,
      parseDevice
    }
  }
}
</script>

<style scoped>
.muted { color: var(--text-secondary); font-weight: 400; }
.nowrap { white-space: nowrap; }
.logs-scroll {
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}
.service { color: #ffffff; }
.metadata-pre {
  background-color: #0f0f23;
  padding: 6px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 10px;
  max-height: 80px;
  overflow-y: auto;
  min-width: 150px;
  max-width: 250px;
  margin: 0;
}
.additional-info {
  display: inline-block;
  max-width: 200px;
  font-size: 10px;
  color: #a9a9b3;
}
.app-version {
  color: #16c79a;
  font-weight: 600;
}
.build-number {
  color: #6c63ff;
  font-weight: 600;
  font-size: 11px;
}
.device {
  display: inline-block;
  max-width: 150px;
  font-size: 10px;
  color: #a9a9b3;
}
.user-id-link {
  color: #6c63ff;
  text-decoration: underline;
  cursor: pointer;
}
.user-id-link:hover {
  color: #8b84ff;
}
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  color: #a9a9b3;
  font-size: 13px;
}
.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(108, 99, 255, 0.3);
  border-radius: 50%;
  border-top-color: #6c63ff;
  animation: spin 0.8s linear infinite;
}
.end-message {
  text-align: center;
  padding: 16px;
  color: #6c63ff;
  font-size: 12px;
  font-weight: 500;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
